import React, { useState } from 'react';

// ═══════════════════════════════════════════════════════════════════
// DYNAMIC PHOTO GALLERY COMPONENT
// Creates visually interesting layouts for any number of photos
// ═══════════════════════════════════════════════════════════════════

export function PhotoGallery({ images = [] }) {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || images.length === 0) return null;

  // For 7-8 images, create a magazine-style layout
  const renderMagazineLayout = () => {
    const count = images.length;

    if (count === 7) {
      // 1 large hero + 6 smaller images in a grid
      return (
        <div className="photo-gallery photo-gallery--magazine-7">
          <div
            className="photo-gallery__item photo-gallery__item--hero"
            onClick={() => setSelectedImage(images[0])}
          >
            {renderImage(images[0], 0)}
          </div>
          <div className="photo-gallery__grid">
            {images.slice(1).map((image, index) => (
              <div
                key={index + 1}
                className="photo-gallery__item"
                onClick={() => setSelectedImage(image)}
              >
                {renderImage(image, index + 1)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (count === 8) {
      // 2 large images + 6 smaller in a grid
      return (
        <div className="photo-gallery photo-gallery--magazine-8">
          <div className="photo-gallery__featured">
            <div
              className="photo-gallery__item photo-gallery__item--large"
              onClick={() => setSelectedImage(images[0])}
            >
              {renderImage(images[0], 0)}
            </div>
            <div
              className="photo-gallery__item photo-gallery__item--large"
              onClick={() => setSelectedImage(images[1])}
            >
              {renderImage(images[1], 1)}
            </div>
          </div>
          <div className="photo-gallery__grid">
            {images.slice(2).map((image, index) => (
              <div
                key={index + 2}
                className="photo-gallery__item"
                onClick={() => setSelectedImage(image)}
              >
                {renderImage(image, index + 2)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderImage = (image, index) => {
    if (image.src) {
      return (
        <>
          <img
            src={image.src}
            alt={image.caption || `Photo ${index + 1}`}
            className="photo-gallery__image"
            loading="lazy"
          />
          {image.caption && (
            <div className="photo-gallery__caption">{image.caption}</div>
          )}
        </>
      );
    } else {
      return (
        <div className="photo-gallery__placeholder">
          <span className="photo-gallery__placeholder-icon">📷</span>
        </div>
      );
    }
  };

  // Determine layout based on number of images
  const getLayout = () => {
    const count = images.length;

    // Special magazine layouts for 7-8 images
    if (count === 7 || count === 8) {
      return renderMagazineLayout();
    }

    // Dynamic class-based layouts for other counts
    let layoutClass = "";

    if (count === 1) layoutClass = "photo-gallery--single";
    else if (count === 2) layoutClass = "photo-gallery--two";
    else if (count === 3) layoutClass = "photo-gallery--three";
    else if (count === 4) layoutClass = "photo-gallery--four";
    else if (count === 5) layoutClass = "photo-gallery--five";
    else if (count === 6) layoutClass = "photo-gallery--six";
    else if (count >= 9 && count <= 12) layoutClass = "photo-gallery--mosaic";
    else if (count > 12) layoutClass = "photo-gallery--masonry";

    return (
      <div className={`photo-gallery ${layoutClass}`}>
        {images.map((image, index) => {
          // For 5 images, make first 2 larger
          const isLarge = count === 5 && index < 2;
          // For mosaic layout (9-12 images), make some images larger
          const isFeatured = (count >= 9 && count <= 12) && (index === 0 || index === 4 || index === 8);

          return (
            <div
              key={index}
              className={`photo-gallery__item ${isLarge ? 'photo-gallery__item--large' : ''} ${isFeatured ? 'photo-gallery__item--featured' : ''}`}
              onClick={() => setSelectedImage(image)}
            >
              {renderImage(image, index)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {getLayout()}

      {/* Lightbox overlay */}
      {selectedImage && (
        <div className="photo-lightbox" onClick={() => setSelectedImage(null)}>
          <div className="photo-lightbox__content">
            <button
              className="photo-lightbox__close"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              ✕
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.caption || "Photo"}
              className="photo-lightbox__image"
            />
            {selectedImage.caption && (
              <div className="photo-lightbox__caption">
                {selectedImage.caption}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Photo component for backward compatibility
export function Photo({ src, caption, aspect = "3/2" }) {
  if (src) {
    return (
      <div className="photo-gallery__item" style={{ aspectRatio: aspect }}>
        <img
          src={src}
          alt={caption || "Photo"}
          className="photo-gallery__image"
          loading="lazy"
        />
        {caption && <p className="photo-gallery__caption">{caption}</p>}
      </div>
    );
  }

  // Placeholder for when no image is provided
  return (
    <div className="photo-placeholder" style={{ aspectRatio: aspect }}>
      <div className="photo-placeholder__pattern" />
      <div className="photo-placeholder__content">
        <div className="photo-placeholder__icon">📷</div>
        <div className="photo-caption">{caption}</div>
      </div>
    </div>
  );
}